import * as React from "react"
import { useEditorState } from "@tiptap/react"
import { Button } from "../../tiptap-ui-primitive/button"
import { useTiptapEditor } from "../../hooks/use-tiptap-editor"
import "./color-highlight-popover.scss"

const COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
  "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
]

export const ColorHighlightPopover: React.FC = () => {
  const editor = useTiptapEditor()
  const [isOpen, setIsOpen] = React.useState(false)
  const [mode, setMode] = React.useState<"text" | "highlight">("text")
  // Сохраняем позицию селекции при открытии
  const savedSelectionRef = React.useRef<{ from: number; to: number } | null>(null)

  const state = useEditorState({
    editor,
    selector: (ctx) => ({
      textColor: ctx.editor?.getAttributes("textStyle").color ?? null,
      highlightColor: ctx.editor?.getAttributes("highlight").color ?? null,
    }),
  })

  const handleOpen = () => {
    // Сохраняем текущую селекцию
    if (editor) {
      const { from, to } = editor.state.selection
      savedSelectionRef.current = { from, to }
    }
    setIsOpen(!isOpen)
  }

  const handleColorSelect = (color: string) => {
    if (!editor) return
    
    // Восстанавливаем селекцию
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current
      editor.chain().setTextSelection({ from, to }).run()
    }
    
    if (mode === "text") {
      editor.chain().focus().setColor(color).run()
    } else {
      editor.chain().focus().setHighlight({ color }).run()
    }
    setIsOpen(false)
    savedSelectionRef.current = null
  }

  const handleClear = () => {
    if (!editor) return
    
    // Восстанавливаем селекцию
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current
      editor.chain().setTextSelection({ from, to }).run()
    }
    
    if (mode === "text") {
      editor.chain().focus().unsetColor().run()
    } else {
      editor.chain().focus().unsetHighlight().run()
    }
    setIsOpen(false)
    savedSelectionRef.current = null
  }

  return (
    <div className="tiptap-color-popover-wrapper">
      <Button
        onClick={handleOpen}
        aria-label="Цвет текста"
        title="Цвет текста"
      >
        <span style={{ 
          borderBottom: `3px solid ${mode === "text" ? state.textColor || "currentColor" : state.highlightColor || "yellow"}` 
        }}>
          A
        </span>
      </Button>
      {isOpen && (
        <div className="tiptap-color-popover">
          <div className="tiptap-color-popover-tabs">
            <Button 
              onClick={() => setMode("text")} 
              data-active={mode === "text"}
            >
              Текст
            </Button>
            <Button 
              onClick={() => setMode("highlight")} 
              data-active={mode === "highlight"}
            >
              Выделение
            </Button>
          </div>
          <div className="tiptap-color-popover-grid">
            {COLORS.map((color) => (
              <button
                key={color}
                className="tiptap-color-swatch"
                style={{ backgroundColor: color }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleColorSelect(color)}
                title={color}
              />
            ))}
          </div>
          <Button onClick={handleClear} data-style="ghost" style={{ width: "100%" }}>
            Сбросить
          </Button>
        </div>
      )}
    </div>
  )
}
